/**
 * API Client
 * Configured Axios instance with HTTP-only cookie authentication
 */

import axios, { AxiosError } from 'axios';
import type { ApiResponse, ApiErrorResponse } from './types';

// Get configuration from environment variables
// Orden de prioridad:
// 1. VITE_API_URL explícita (env variable)
// 2. Proxy relativo en desarrollo local
// 3. Fallback para producción
const API_URL = import.meta.env.VITE_API_URL
  ? (import.meta.env.VITE_API_URL as string)
  : import.meta.env.DEV
    ? '/api' // ← Usa el proxy de Vite en desarrollo local
    : 'http://localhost:3000/api'; // ← Fallback para producción
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT as string) || 30000;

/**
 * Callback for handling 401 errors (unauthorized)
 * Set this from the app component that has access to navigation
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

/**
 * Axios instance with base configuration
 */
export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable sending cookies with requests (for HTTP-only cookies)
  withCredentials: true,
});

/**
 * Response interceptor - handles common response patterns and errors
 */
apiClient.interceptors.response.use(
  (response) => {
    // Extract data from response wrapper
    return response.data;
  },
  (error: AxiosError<ApiErrorResponse>) => {
    // Handle 401 Unauthorized - call the handler if set
    // The HTTP-only cookie will be cleared by the backend
    if (error.response?.status === 401) {
      if (onUnauthorized) {
        onUnauthorized();
      }
    }

    // Extract error message
    const errorMessage =
      error.response?.data?.error?.message || error.message || 'Ocurrió un error inesperado';

    // Return a rejected promise with the error
    return Promise.reject(new Error(errorMessage));
  }
);

/**
 * Type-safe API response helper
 */
export type ApiResponseData<T> = T extends ApiResponse<infer U> ? U : never;

/**
 * Export types for convenience
 */
export type { ApiResponse, ApiErrorResponse } from './types';
